import type { IAgoraRTCRemoteUser } from 'agora-rtc-sdk-ng'
import type { ICameraVideoTrack, ILocalVideoTrack } from 'agora-rtc-sdk-ng'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import type { LoginRes } from '@/api/login'
import { DEFAULT_TUTOR_CALL_MEDIA_UI, type TutorCallMediaUi } from '@/tutorCall/callMediaUi'
import { bizUserId, type InvitePayload, loadStoredRole, parseTeacherFromMember, persistRole, type TutorRole } from '@/tutorCall/constants'

export type TutorCallPhase = 'idle' | 'outgoing' | 'incoming' | 'connected'

/** 大厅内教师可呼叫状态（由 ArtTutorCallLobbyState 同步） */
export type TeacherLobbyCard = { displayName: string; availableForCall: boolean; avatarUrl: string }

interface TutorCallState {
  persistedRole: TutorRole | null
  pickedRole: TutorRole | null
  phase: TutorCallPhase
  incomingInvite: InvitePayload | null
  pickTeacherOpen: boolean
  onlineTeachers: { rtmId: string; userId: string }[]
  remoteName: string
  localVideoTrack: ICameraVideoTrack | ILocalVideoTrack | null
  remoteUser: IAgoraRTCRemoteUser | null
  rtmLobbyReady: boolean
  /** 呼叫流程：拉老师列表 / 建 RTC / 发信令 等异步阶段 */
  callFlowLoading: boolean
  teacherLobbyCards: Record<string, TeacherLobbyCard>
  /** 浮窗标题旁：网络/双方麦摄状态；本地麦摄由开关同步 */
  callMediaUi: TutorCallMediaUi

  syncUser: (user: LoginRes['data'] | null) => void
  pickRole: (user: LoginRes['data'], role: TutorRole) => void
  setPhase: (phase: TutorCallPhase) => void
  setIncomingInvite: (v: InvitePayload | null) => void
  setPickTeacherOpen: (open: boolean) => void
  setOnlineTeachers: (list: { rtmId: string; userId: string }[]) => void
  setRemoteName: (name: string) => void
  setLocalVideoTrack: (t: ICameraVideoTrack | ILocalVideoTrack | null) => void
  setRemoteUser: (u: IAgoraRTCRemoteUser | null) => void
  setRtmLobbyReady: (v: boolean) => void
  setCallFlowLoading: (v: boolean) => void
  setTeacherLobbyCard: (teacherBizId: string, card: TeacherLobbyCard) => void
  /** RTM 大厅 REMOTE_LEAVE：从选老师列表与名片缓存中移除该教师 */
  removeLobbyTeacherByPublisher: (rtmPublisherId: string) => void
  resetCallMedia: () => void
  patchCallMediaUi: (partial: Partial<TutorCallMediaUi>) => void
}

export const useTutorCallStore = create<TutorCallState>()(
  devtools(
    immer((set) => ({
      persistedRole: null,
      pickedRole: null,
      phase: 'idle',
      incomingInvite: null,
      pickTeacherOpen: false,
      onlineTeachers: [],
      remoteName: '',
      localVideoTrack: null,
      remoteUser: null,
      rtmLobbyReady: false,
      callFlowLoading: false,
      teacherLobbyCards: {},
      callMediaUi: { ...DEFAULT_TUTOR_CALL_MEDIA_UI },

      syncUser: (user) =>
        set((state) => {
          if (!user) {
            state.persistedRole = null
            state.pickedRole = null
            state.phase = 'idle'
            state.incomingInvite = null
            state.pickTeacherOpen = false
            state.onlineTeachers = []
            state.remoteName = ''
            state.localVideoTrack = null
            state.remoteUser = null
            state.rtmLobbyReady = false
            state.callFlowLoading = false
            state.teacherLobbyCards = {}
            state.callMediaUi = { ...DEFAULT_TUTOR_CALL_MEDIA_UI }
            return
          }
          state.persistedRole = loadStoredRole(bizUserId(user))
        }),

      pickRole: (user, role) =>
        set((state) => {
          persistRole(bizUserId(user), role)
          state.pickedRole = role
        }),

      setPhase: (phase) =>
        set((state) => {
          state.phase = phase
        }),

      setIncomingInvite: (v) =>
        set((state) => {
          state.incomingInvite = v
        }),

      setPickTeacherOpen: (open) =>
        set((state) => {
          state.pickTeacherOpen = open
        }),

      setOnlineTeachers: (list) =>
        set((state) => {
          state.onlineTeachers = list
        }),

      setRemoteName: (name) =>
        set((state) => {
          state.remoteName = name
        }),

      setLocalVideoTrack: (t) =>
        set((state) => {
          state.localVideoTrack = t
        }),

      setRemoteUser: (u) =>
        set((state) => {
          state.remoteUser = u
        }),

      setRtmLobbyReady: (v) =>
        set((state) => {
          state.rtmLobbyReady = v
        }),

      setCallFlowLoading: (v) =>
        set((state) => {
          state.callFlowLoading = v
        }),

      setTeacherLobbyCard: (teacherBizId, card) =>
        set((state) => {
          state.teacherLobbyCards[teacherBizId] = card
        }),

      removeLobbyTeacherByPublisher: (rtmPublisherId) =>
        set((state) => {
          const pid = rtmPublisherId.trim()
          if (!pid.startsWith('10')) return
          state.onlineTeachers = state.onlineTeachers.filter((t) => t.rtmId !== pid)
          const bizId = parseTeacherFromMember(pid)
          if (bizId) delete state.teacherLobbyCards[bizId]
        }),

      resetCallMedia: () =>
        set((state) => {
          state.localVideoTrack = null
          state.remoteUser = null
          state.callMediaUi = { ...DEFAULT_TUTOR_CALL_MEDIA_UI }
        }),

      patchCallMediaUi: (partial) =>
        set((state) => {
          Object.assign(state.callMediaUi, partial)
        })
    })),
    { name: 'tutor-call-store' }
  )
)

export function selectTutorEffectiveRole(s: TutorCallState): TutorRole | null {
  return s.pickedRole ?? s.persistedRole
}

export function selectTutorNeedPickRole(user: LoginRes['data'] | null, s: TutorCallState): boolean {
  return Boolean(user && selectTutorEffectiveRole(s) == null)
}
